import React, { useMemo, memo } from 'react';
import type { InstagramCreatorDailyEntry } from '@frontend/gql/social/graphql';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { SocialStatsIcon } from '../../../../components/icons/social-stats-icon';
import { SocialStatsHighlightTab } from '../social-stats-highlight-tab';
import type { SocialStatsSectionProps } from './hooks';
import { SocialStatsChartView, type SocialStatsChartDataItem } from './views/social-stats-chart-view';

const SOCIAL_STATS_TABS = [
  { key: 'engagements', heading: 'Engagements', info: 'View Details' },
  { key: 'reach', heading: 'Reach', info: 'View Details' },
] as const;

const transformToChartData = (rawData: InstagramCreatorDailyEntry[]): SocialStatsChartDataItem[] => {
  return rawData.reduce((acc: SocialStatsChartDataItem[], item) => {
    if (item.date != null && item.value != null) {
      try {
        // Parse the date, handling both string and Date object inputs
        const date = item.date instanceof Date ? item.date : new Date(item.date);
        // Validate the date is valid
        if (isNaN(date.getTime())) {
          console.warn(`Invalid date encountered: ${item.date}`);
          return acc;
        }

        // Format to YYYY-MM-DD in UTC to ensure consistency
        const dateString = date.toISOString().split('T')[0];
        acc.push({
          xValue: dateString,
          yValue: item.value,
        });
      } catch (error) {
        console.warn(`Error processing date ${item.date}:`, error);
        return acc;
      }
    }
    return acc;
  }, []);
};

export const SocialStatsSection: React.FC<SocialStatsSectionProps> = memo(({
  activeSocialStatTab,
  setActiveSocialStatTab,
  isLoading,
  engagementsData,
  reachData,
}) => {
  const currentChartData = useMemo(() => {
    if (activeSocialStatTab === 'engagements') {
      return transformToChartData(engagementsData);
    } else if (activeSocialStatTab === 'reach') {
      return transformToChartData(reachData);
    }
    return [];
  }, [activeSocialStatTab, engagementsData, reachData]);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Skeleton className="w-6 h-6 p-1 rounded-lg" />
          <Skeleton className="h-5 w-1/3" />
        </div>
        <div className="flex flex-col space-y-2 mt-2">
          <div className="flex space-x-2">
            <Skeleton className="w-[200px] h-[62px] rounded-xl" />
            <Skeleton className="w-[200px] h-[62px] rounded-xl" />
          </div>
          <Skeleton className="h-[200px] w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
        <div className="w-6 h-6 p-1 rounded-lg bg-[#6C77DE] flex items-center justify-center">
          <SocialStatsIcon />
        </div>
        <span>Social Stats</span>
      </h3>
      <div className="flex flex-col space-y-2 mt-2">
        <div className="flex space-x-2">
          {SOCIAL_STATS_TABS.map((tab) => (
            <SocialStatsHighlightTab
              key={tab.key}
              headingText={tab.heading}
              infoText={tab.info}
              isActive={activeSocialStatTab === tab.key}
              onClick={() => setActiveSocialStatTab(tab.key)}
            />
          ))}
        </div>
        <h4 className="text-sm text-muted-foreground capitalize">
          {activeSocialStatTab}
        </h4>
        <div className="rounded-lg w-full flex items-center justify-center">
          <SocialStatsChartView
            activeTabKey={activeSocialStatTab}
            data={currentChartData}
          />
        </div>
      </div>
    </div>
  );
});

SocialStatsSection.displayName = 'SocialStatsSection';
